// Parent Homework — printable practice sheet (Track B). One A4 sheet for the child's whole active
// plan: header (child · specialist · week), one block per exercise (title · instructions · schedule ·
// photo/QR) with a 7-day checkbox grid (rows = the exercise's slots, off-schedule days greyed), footer.
// Opened in a NEW TAB (the codebase's iOS-safe window.open pattern) so Safari's Print/Share work.
// Images are re-signed into the tab (signed URLs valid ~900s). QR for video exercises lands in SC3.

import qrcode from 'qrcode-generator';
import { isExerciseScheduledOn, resolveExerciseSchedule, exerciseSlotsOn } from '../data.js';
import { _slotName, _recurrenceLabel, _artFor, _dose, _esc, _firstName } from './today.js';

const T = (k, p) => window.HUD?.T?.(k, p) || k;
const APP_BASE = 'https://www.huddledin.com'; // QR encodes a login-gated app deep link, NEVER a signed storage URL

// Crisp SVG QR (scales for print). qrcode-generator: type 0 = auto-size, ecc 'M'.
function _qrSvg(url) {
  const qr = qrcode(0, 'M'); qr.addData(url); qr.make();
  const n = qr.getModuleCount();
  let rects = '';
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) rects += `<rect x="${c}" y="${r}" width="1" height="1"/>`;
  return `<svg viewBox="0 0 ${n} ${n}" width="100%" height="100%" shape-rendering="crispEdges" xmlns="http://www.w3.org/2000/svg">` +
    `<rect width="${n}" height="${n}" fill="#fff"/><g fill="#000">${rects}</g></svg>`;
}
const VIDEO_RE = /\.(mp4|mov|webm|m4v|avi|mkv)$/i;
const IMAGE_RE = /\.(png|jpe?g|gif|webp|heic|heif)$/i;
const DAY_KEYS = ['cal_day_su', 'cal_day_mo', 'cal_day_tu', 'cal_day_we', 'cal_day_th', 'cal_day_fr', 'cal_day_sa'];

// First media attachment classified (path-first, per the lazy-sign rule).
function _firstMedia(ex) {
  const names = ex.attached_file_names || [], paths = ex.attached_file_paths || [];
  const n = Math.max(names.length, paths.length);
  for (let i = 0; i < n; i++) {
    const nm = names[i] || '';
    if (VIDEO_RE.test(nm)) return { kind: 'video', path: paths[i] || null };
    if (IMAGE_RE.test(nm)) return { kind: 'image', path: paths[i] || null };
  }
  return { kind: 'none', path: null };
}

function _sign(path, H) {
  if (!path) return Promise.resolve(null);
  try {
    if (typeof H._signFilePool === 'function') return H._signFilePool(path).catch(() => null);
    if (H.SB?.signFile) return H.SB.signFile(path).catch(() => null);
  } catch (_) {}
  return Promise.resolve(null);
}

// Sunday-first week (Israel) containing today → 7 Date objects.
function _weekDates() {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  const start = new Date(t); start.setDate(t.getDate() - t.getDay());
  return Array.from({ length: 7 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
}

function _dateStr(d, rtl) {
  try { return d.toLocaleDateString(rtl ? 'he-IL' : 'en-US', { month: 'short', day: 'numeric' }); }
  catch (_) { return window.HUD._ymd(d); }
}

// Full "Sunday, Sep 21" style line for the daily sheet header.
function _fullDate(d, rtl) {
  try { return d.toLocaleDateString(rtl ? 'he-IL' : 'en-US', { weekday: 'long', month: 'short', day: 'numeric' }); }
  catch (_) { return window.HUD._ymd(d); }
}

const _SHEET_CSS = `
*{box-sizing:border-box;margin:0;padding:0}
body{font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;color:#1a2e2b;background:#9aa8a6;padding:24px 12px;display:flex;flex-direction:column;align-items:center}
body[dir="rtl"]{font-family:'Heebo','Segoe UI',system-ui,sans-serif}
.sheet{width:210mm;min-height:297mm;background:#fff;box-shadow:0 8px 32px rgba(0,0,0,.25);padding:14mm 16mm 12mm;display:flex;flex-direction:column}
.hd{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:2.5px solid #0d9488;padding-bottom:5mm;margin-bottom:6mm;gap:8mm}
.hd .label{font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#0d9488;margin-bottom:4px}
.hd .title{font-size:24px;font-weight:600;line-height:1.2}
.hd .title .cn{color:#0d6b63}
.hd .meta{text-align:end;font-size:12px;color:#4a7570;line-height:1.55}
.hd .meta .sp{font-weight:500;color:#1a2e2b}
.ex{border:1px solid #c4dbd8;border-radius:14px;padding:5mm 6mm;margin-bottom:5mm;break-inside:avoid}
.ex-top{display:flex;gap:6mm;align-items:flex-start}
.ex-body{flex:1;min-width:0}
.ex-num{display:inline-flex;align-items:center;justify-content:center;width:18px;height:18px;border-radius:50%;border:1.5px solid #0d9488;color:#0d9488;font-size:11px;font-weight:600;margin-inline-end:7px;flex-shrink:0;vertical-align:-3px}
.ex-title{font-size:16px;font-weight:600;margin-bottom:2.5mm}
.ex-instr{font-size:12.5px;line-height:1.55;margin-bottom:2.5mm;white-space:pre-wrap;overflow-wrap:anywhere}
.hw-note{border:1px solid #c4dbd8;background:#f5fafa;border-radius:14px;padding:4mm 6mm;margin-bottom:5mm}
.hw-note-h{font-size:11px;font-weight:600;letter-spacing:.08em;text-transform:uppercase;color:#0d9488;margin-bottom:2mm}
.hw-note-t{font-size:12.5px;line-height:1.55;white-space:pre-wrap;overflow-wrap:anywhere}
.ex-dose{font-size:13.5px;font-weight:700;color:#0d6b63;margin-bottom:1.5mm}
.ex-sched{font-size:11.5px;font-weight:500;color:#4a7570}
.ex-photo{width:40mm;height:40mm;flex-shrink:0;border-radius:10px;border:1px solid #c4dbd8;background:#f5fafa;display:flex;align-items:center;justify-content:center;font-size:44px;overflow:hidden}
.ex-photo img{width:100%;height:100%;object-fit:cover}
.ex-video{width:40mm;flex-shrink:0;border:1.5px solid #0d9488;border-radius:10px;padding:3mm;display:flex;flex-direction:column;align-items:center;gap:2mm}
.ex-video .qr{width:28mm;height:28mm}
.ex-video .qr-cap{font-size:9.5px;font-weight:500;text-align:center;line-height:1.3;color:#1a2e2b}
.grid{margin-top:3.5mm;border-top:1px dashed #c4dbd8;padding-top:3mm}
.grid table{border-collapse:collapse;width:100%}
.grid th{font-size:9.5px;font-weight:600;color:#4a7570;text-transform:uppercase;letter-spacing:.04em;padding-bottom:1.5mm;text-align:center}
.grid th.sl,.grid td.sl{text-align:start;width:24mm;font-size:10px;color:#4a7570;font-weight:400;text-transform:none;letter-spacing:0}
.grid td{text-align:center;padding:1mm 0}
.grid.day table{width:auto}
.grid.day th,.grid.day td{padding-inline-end:8mm}
.cbx{display:inline-block;width:5mm;height:5mm;border:1.5px solid #0d9488;border-radius:2px;background:#fff}
.cbx.off{border-color:#c4dbd8}
.ft{margin-top:auto;padding-top:4mm;border-top:1px solid #e8f4f2;display:flex;justify-content:space-between;align-items:center;font-size:10px;color:#4a7570}
.ft .wm{font-size:12px;font-weight:600;color:#0d9488}
@page{size:A4 portrait;margin:0}
@media print{body{background:#fff;padding:0}.sheet{box-shadow:none;width:auto;min-height:auto}}
`;

// scope 'week' → 7-day column grid; 'day' → today's slots, one checkbox each (today var required).
function _exerciseBlock(hw, ex, num, week, mediaUrl, mediaKind, childId, scope, today) {
  const isDay = scope === 'day';
  const instr = (ex.instructions || '').trim();

  let media;
  if (mediaKind === 'image' && mediaUrl) media = `<div class="ex-photo"><img src="${_esc(mediaUrl)}" alt=""></div>`;
  else if (mediaKind === 'video') {
    // Login-gated app deep link (never a signed storage URL — those expire; the app re-signs on open).
    const link = `${APP_BASE}/?tab=homework&child=${encodeURIComponent(childId || '')}&hw=${encodeURIComponent(hw.id)}&ex=${encodeURIComponent(ex.id)}`;
    media = `<div class="ex-video"><div class="qr">${_qrSvg(link)}</div><div class="qr-cap">${_esc(T('hwp_scan_video'))}</div></div>`;
  } else media = `<div class="ex-photo">${_artFor(ex.id)}</div>`; // deterministic art fallback

  let schedLine, headRow, bodyRows;
  if (isDay) {
    // Only the slots actually scheduled today, one checkbox each.
    const slots = exerciseSlotsOn(hw, ex, today);
    const useSlots = slots.length ? slots : ['morning'];
    schedLine = useSlots.map(_slotName).join(' · ') + ' · ' + _recurrenceLabel(hw);
    headRow = `<tr><th class="sl"></th><th>${_esc(T('hw5_today'))}</th></tr>`;
    bodyRows = useSlots.map(s => `<tr><td class="sl">${_esc(_slotName(s))}</td><td><span class="cbx"></span></td></tr>`).join('');
  } else {
    const sched = resolveExerciseSchedule(hw, ex);
    const slots = sched.timeSlots.length ? sched.timeSlots : ['morning'];
    const days = week.map(d => isExerciseScheduledOn(hw, ex, d));
    schedLine = slots.map(_slotName).join(' · ') + ' · ' + _recurrenceLabel(hw);
    headRow = '<tr><th class="sl"></th>' + DAY_KEYS.map(k => `<th>${_esc(T(k))}</th>`).join('') + '</tr>';
    bodyRows = slots.map(s =>
      `<tr><td class="sl">${_esc(_slotName(s))}</td>` +
      days.map(on => `<td><span class="cbx${on ? '' : ' off'}"></span></td>`).join('') + '</tr>'
    ).join('');
  }

  return `<section class="ex"><div class="ex-top"><div class="ex-body">` +
    `<div class="ex-title"><span class="ex-num">${num}</span>${_esc(ex.title || T('hw3_exercise'))}</div>` +
    (instr ? `<p class="ex-instr" dir="auto">${_esc(instr)}</p>` : '') +
    `<div class="ex-dose">${_esc(_dose(ex))}</div>` +
    `<div class="ex-sched">${_esc(schedLine)}</div></div>${media}</div>` +
    `<div class="grid${isDay ? ' day' : ''}"><table>${headRow}${bodyRows}</table></div></section>`;
}

// F-104: the specialist's homework-level note, once per homework, above that homework's first printed exercise. Reuses the approved
// "Instructions from {name}" heading (hw6_info_title); plain text, escaped; empty or whitespace-only = no block.
function _noteBlock(hw) {
  const note = String(hw.description || '').trim();
  if (!note) return '';
  return `<section class="hw-note"><div class="hw-note-h">${_esc(T('hw6_info_title', { name: _firstName(hw.specialist_name) }))}</div><p class="hw-note-t" dir="auto">${_esc(note)}</p></section>`;
}

function _buildHtml(homeworks, child, H, signed, childId, scope) {
  const rtl = !!(H._isRtl ? H._isRtl() : (H._lang === 'he'));
  const isDay = scope === 'day';
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const week = _weekDates();
  const specs = H.DB?.specialists || [];
  // Header specialist: shown only if the whole plan is one specialist (else per-exercise, omitted here).
  const specIds = [...new Set(homeworks.map(h => h.specialist_id))];
  let headSpec = '';
  if (specIds.length === 1) {
    const s = specs.find(x => x.id === specIds[0]);
    const nm = s?.name || homeworks[0]?.specialist_name || '';
    const role = s?.role || '';
    if (nm) headSpec = `<div class="sp">${_esc(nm)}${role ? ', ' + _esc(role) : ''}</div>`;
  }
  // Daily sheet → today's date; weekly sheet → the Sunday–Saturday span.
  const dateLine = isDay ? _fullDate(today, rtl) : `${_dateStr(week[0], rtl)} – ${_dateStr(week[6], rtl)}`;
  const titleStr = T(isDay ? 'hwp_sheet_title_day' : 'hwp_sheet_title');

  let num = 0, blocks = '';
  homeworks.forEach(hw => {
    let noted = false;
    (hw.exercises || []).forEach(ex => {
      if (isDay && !isExerciseScheduledOn(hw, ex, today)) return; // daily sheet: today's exercises only
      if (!noted) { noted = true; blocks += _noteBlock(hw); }
      num++;
      const m = _firstMedia(ex);
      blocks += _exerciseBlock(hw, ex, num, week, signed[m.path] || null, m.kind, childId, scope, today);
    });
  });

  const childName = child?.name || T('hw3_exercise');
  return `<!DOCTYPE html><html lang="${rtl ? 'he' : 'en'}" dir="${rtl ? 'rtl' : 'ltr'}"><head><meta charset="UTF-8">` +
    `<meta name="viewport" content="width=device-width,initial-scale=1"><title>${_esc(titleStr)}</title>` +
    `<style>${_SHEET_CSS}</style></head><body dir="${rtl ? 'rtl' : 'ltr'}"><div class="sheet">` +
    `<header class="hd"><div><div class="label">${_esc(titleStr)}</div>` +
    `<div class="title"><span class="cn">${_esc(childName)}</span></div></div>` +
    `<div class="meta">${headSpec}<div>${_esc(dateLine)}</div></div></header>` +
    blocks +
    `<footer class="ft"><div>${_esc(T('hwp_made_with'))} · huddledin.com</div><div class="wm">huddled·in</div></footer>` +
    `</div></body></html>`;
}

// Entry: open the sheet in a new tab. childId + homeworks come from the parent surface.
// scope: 'week' = whole active plan + 7-day grid (default); 'day' = today's exercises + today's slots (SC4b).
export async function printHomeworkSheet(homeworks, childId, H, scope = 'week') {
  const active = (homeworks || []).filter(hw => !hw.is_paused && hw.status === 'active' && (hw.exercises || []).length);
  if (!active.length) { H.toast?.(H.T ? H.T('hw3_no_active') : 'No active homework.', 'info'); return; }
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const inScope = (hw, ex) => scope !== 'day' || isExerciseScheduledOn(hw, ex, today);
  // Daily sheet with nothing scheduled today → nothing to print.
  if (scope === 'day' && !active.some(hw => (hw.exercises || []).some(ex => isExerciseScheduledOn(hw, ex, today)))) {
    H.toast?.(H.T ? H.T('hw5_not_today') : 'Nothing scheduled today.', 'info'); return;
  }
  const tab = window.open('about:blank', '_blank'); // sync open — required for iOS Safari
  try {
    const child = (H.DB?.children || []).find(c => c.id === childId) || null;
    // Re-sign every in-scope image attachment (path-first) into a { path: url } map.
    const imgPaths = [];
    active.forEach(hw => (hw.exercises || []).forEach(ex => { if (!inScope(hw, ex)) return; const m = _firstMedia(ex); if (m.kind === 'image' && m.path) imgPaths.push(m.path); }));
    const urls = await Promise.all(imgPaths.map(p => _sign(p, H)));
    const signed = {}; imgPaths.forEach((p, i) => { signed[p] = urls[i]; });
    const html = _buildHtml(active, child, H, signed, childId, scope);
    if (tab) { tab.document.open(); tab.document.write(html); tab.document.close(); }
  } catch (e) {
    console.error('❌ print sheet:', e);
    if (tab) try { tab.close(); } catch (_) {}
    H.toast?.(H.T ? H.T('err_generic') : 'Could not build the sheet.', 'error');
  }
}
